import { useCallback, useMemo } from 'react';
import { CircleAlert, CirclePlay, Globe, Wrench } from 'lucide-react';
import criticalIcon from '@assets/svg/New_Severity_Icons/Critical.svg';
import highIcon from '@assets/svg/New_Severity_Icons/High.svg';
import lowIcon from '@assets/svg/New_Severity_Icons/Low.svg';
import mediumIcon from '@assets/svg/New_Severity_Icons/Medium.svg';
import unknownIcon from '@assets/svg/workload/info.svg';
import ResourceFilterCard from '@root/pages/inventory-overview/resource-evaluation/ResourceFilterCard';

// Chip filters above the Findings and CVEs tables. The chips hold no state of their own: they read
// and write the table's `selectedFilters`, so they stay in step with the filter panel and with the
// dashboard funnel deep links that seed those filters.

// Unknown uses the same fallback icon SeverityIcons shows for an unrecognised severity.
const SEVERITY_ICONS = {
  Critical: criticalIcon,
  High: highIcon,
  Medium: mediumIcon,
  Low: lowIcon,
  Unknown: unknownIcon,
};
const SEVERITY_CHIPS = Object.keys(SEVERITY_ICONS).map(name => ({ name }));

// Each chip switches one boolean table filter on; the icons and colours match the dashboard's
// runtime exploit funnel. "Critical" has no boolean filter of its own and toggles the critical
// severity instead, so it lights up together with the Critical severity chip.
const RISK_CHIPS = [
  { name: 'Package in use', filterKey: 'in_use', icon: CirclePlay, iconColor: '#22A06B' },
  { name: 'Internet exposure', filterKey: 'is_public_exposed', icon: Globe, iconColor: '#3BA4E8' },
  { name: 'Known exploit', filterKey: 'exploitable', icon: CircleAlert, iconColor: '#E5484D' },
  { name: 'Fix available', filterKey: 'fixable', icon: Wrench, iconColor: '#F59E0B' },
  // { name: 'Critical', severity: 'critical' },
];

// The filter panel may hold `'true'` where the deep links hold `true`.
const isTrue = value => String(value) === 'true';

const hasSeverity = (values = [], severity) => values.some(value => value?.toLowerCase() === severity);

// Adds or removes one severity, leaving any other severity (e.g. one picked in the filter panel) as is.
const withSeverity = (values = [], severity, isOn) => {
  const rest = values.filter(value => value?.toLowerCase() !== severity);
  return isOn ? [...rest, severity] : rest;
};

const NO_HIDDEN_RISK_FILTERS = [];

// `hiddenRiskFilters` lists the filterKeys whose chips a table's API cannot filter on.
const CveFilterCard = ({ selectedFilters, setSelectedFilters, hiddenRiskFilters = NO_HIDDEN_RISK_FILTERS }) => {
  const riskChips = useMemo(
    () => RISK_CHIPS.filter(({ filterKey }) => !hiddenRiskFilters.includes(filterKey)),
    [hiddenRiskFilters],
  );

  const handleSeverityChange = useCallback(
    next =>
      setSelectedFilters(prev => ({
        ...prev,
        severity: SEVERITY_CHIPS.reduce(
          (values, { name }) => withSeverity(values, name.toLowerCase(), next.includes(name)),
          prev.severity,
        ),
      })),
    [setSelectedFilters],
  );

  const handleRiskChange = useCallback(
    next =>
      setSelectedFilters(prev =>
        riskChips.reduce((updated, { name, filterKey, severity }) => {
          const isOn = next.includes(name);
          if (severity) return { ...updated, severity: withSeverity(updated.severity, severity, isOn) };
          // Keep a `false` picked in the filter panel; only the chip's own `true` is switched.
          const others = (updated[filterKey] ?? []).filter(value => !isTrue(value));
          return { ...updated, [filterKey]: isOn ? [...others, true] : others };
        }, prev),
      ),
    [riskChips, setSelectedFilters],
  );

  const severityRow = useMemo(
    () => ({
      key: 'severity',
      label: 'CVE Severity',
      iconMap: SEVERITY_ICONS,
      items: SEVERITY_CHIPS,
      values: SEVERITY_CHIPS.filter(({ name }) => hasSeverity(selectedFilters.severity, name.toLowerCase())).map(
        ({ name }) => name,
      ),
      onChange: handleSeverityChange,
    }),
    [handleSeverityChange, selectedFilters.severity],
  );

  const riskOverviewRow = useMemo(
    () => ({
      key: 'risk_overview',
      label: 'Risk Overview',
      iconMap: { Critical: criticalIcon },
      items: riskChips,
      values: riskChips
        .filter(({ filterKey, severity }) =>
          severity ? hasSeverity(selectedFilters.severity, severity) : (selectedFilters[filterKey] ?? []).some(isTrue),
        )
        .map(({ name }) => name),
      onChange: handleRiskChange,
    }),
    [handleRiskChange, riskChips, selectedFilters],
  );

  return <ResourceFilterCard categoryRows={[severityRow]} riskOverviewRows={[riskOverviewRow]} />;
};

export default CveFilterCard;
